import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface CartItem {
  productId: string;
  variantId: string | null;
  quantity: number;
  name: string;
  price: number;
  imageUrl: string | null;
}

interface CartState {
  items: CartItem[];
  promoCode: string | null;
  discountAmount: number;
  courseId: string | null;

  addItem: (item: Omit<CartItem, 'quantity'> & { quantity?: number }) => void;
  removeItem: (productId: string, variantId?: string | null) => void;
  updateQuantity: (productId: string, quantity: number, variantId?: string | null) => void;
  clearCart: () => void;
  applyPromoCode: (code: string, discount: number) => void;
  removePromoCode: () => void;
  setCourseId: (courseId: string) => void;

  // Computed
  getItemCount: () => number;
  getSubtotal: () => number;
  getTotal: () => number;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      promoCode: null,
      discountAmount: 0,
      courseId: null,

      addItem: (item) => {
        const { items, courseId } = get();

        // If adding from different course, clear cart first
        if (courseId && item.productId && courseId !== get().courseId) {
          // In real app, would show confirmation dialog
        }

        const existingIndex = items.findIndex(
          (i) => i.productId === item.productId && i.variantId === item.variantId
        );

        if (existingIndex >= 0) {
          const updatedItems = [...items];
          updatedItems[existingIndex].quantity += item.quantity || 1;
          set({ items: updatedItems });
        } else {
          set({ items: [...items, { ...item, quantity: item.quantity || 1 }] });
        }
      },

      removeItem: (productId, variantId = null) => {
        set({
          items: get().items.filter(
            (i) => !(i.productId === productId && i.variantId === variantId)
          ),
        });
      },

      updateQuantity: (productId, quantity, variantId = null) => {
        if (quantity <= 0) {
          get().removeItem(productId, variantId);
          return;
        }

        set({
          items: get().items.map((i) =>
            i.productId === productId && i.variantId === variantId
              ? { ...i, quantity }
              : i
          ),
        });
      },

      clearCart: () => {
        set({ items: [], promoCode: null, discountAmount: 0 });
      },

      applyPromoCode: (code, discount) => {
        set({ promoCode: code, discountAmount: discount });
      },

      removePromoCode: () => {
        set({ promoCode: null, discountAmount: 0 });
      },

      setCourseId: (courseId) => {
        set({ courseId });
      },

      getItemCount: () => {
        return get().items.reduce((sum, item) => sum + item.quantity, 0);
      },

      getSubtotal: () => {
        return get().items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      },

      getTotal: () => {
        const subtotal = get().getSubtotal();
        const discount = get().discountAmount;
        const tax = (subtotal - discount) * 0.0825; // 8.25% tax
        return subtotal - discount + tax;
      },
    }),
    {
      name: 'cart-storage',
    }
  )
);
